import Button from 'components/Button';
import Error from 'components/form/Error';
import Input from 'components/form/Inputs/Input';
import Select from 'components/form/Inputs/Select';
import Success from 'components/form/Success';
import Warning from 'components/form/Warning';
import Modal from 'components/Modal';
import ActionModal from 'components/Modal/ActionModal';
import { PageBack, PageHeadContainer } from 'components/PageHead';
import Space from 'components/Space';
import Typography from 'components/Typography';
import INTERVIEW_TYPE, {
  interviewTypes,
  InterviewTypeValues,
} from 'enums/InterviewTypes';
import {
  mentorshipStatusCanCreateInterview,
  MentorshipStatusValues,
} from 'enums/MentorshipStatus';
import { DateTime } from 'luxon';
import { FC, useMemo, useState } from 'react';
import styled from 'styled-components';
import useSWR, { mutate } from 'swr';
import Mentorship from 'types/Mentorship';
import manageTextError from 'utils/manageTextError';
import privateAPI from 'utils/privateAxiosInstance';

const HeadContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  h1 {
    font-weight: 500;
    font-size: 1.5rem;
    margin-right: 1rem;
  }
`;

const Form = styled.form`
  align-self: center;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 40rem;
  row-gap: 1rem;
  h1 {
    color: black;
    margin-bottom: 0.5rem;
  }
  button {
    margin-left: auto;
    margin-right: 0;
  }
`;

const Description = styled.div`
  margin-top: 5rem;
  margin-bottom: 5rem;
  align-self: center;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 40rem;
  row-gap: 1rem;

  color: var(--gray-500);
`;

export type State = {
  date: string;
  time: string;
  type: InterviewTypeValues | '';
  mentorshipId: number;
};
export const initialState: State = {
  date: '',
  time: '',
  type: '',
  mentorshipId: -1,
};

type Errors = Record<'general' | 'date', boolean>;
const initialErrors: Errors = {
  general: false,
  date: false,
};

type Props = {
  state: State;
  setState: React.Dispatch<State>;
  mentorship?: Mentorship | undefined;
  fixedMentee?: boolean;
  backTo: string;
};
const InterviewSuggestForm: FC<Props> = ({
  state,
  setState,
  mentorship,
  fixedMentee,
  backTo,
}) => {
  const { data: mentorships } = useSWR<Mentorship[]>('/mentorship/mentor');

  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<string>('');
  const [errors, setErrors] = useState<Errors>(initialErrors);

  const currentMentorshipCanCreateInterview: boolean = useMemo(() => {
    return !!mentorships?.find(
      (m) =>
        m.id === state.mentorshipId &&
        mentorshipStatusCanCreateInterview.includes(
          m.status as MentorshipStatusValues,
        ),
    );
  }, [mentorships, state]);

  const handleChange: React.ChangeEventHandler<
    HTMLInputElement & HTMLSelectElement
  > = (e) => {
    if (e.target.name === 'mentorshipId') {
      setState({
        ...state,
        [e.target.name]: parseInt(e.target.value),
      });
    } else {
      setState({
        ...state,
        [e.target.name]: e.target.value,
      });
    }
    setSuccess('');
    setErrors(initialErrors);
  };

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();

    if (!currentMentorshipCanCreateInterview) return;

    const date = DateTime.fromFormat(
      `${state.date} ${state.time}`,
      'yyyy-MM-dd HH:mm',
      { zone: 'Europe/Paris' },
    )
      .toUTC()
      .toISO();
    if (new Date(date).getTime() <= new Date().getTime()) {
      return setArtificialOpen(true);
    }
    if (new Date(date).getTime() <= new Date().getTime() + 15 * 60 * 1000) {
      return setTooCloseOpen(true);
    }

    setLoading(true);
    setSuccess('');
    setErrors(initialErrors);
    setArtificialError('');

    privateAPI
      .post(`/interview/`, {
        mentorshipId: state.mentorshipId,
        date,
        type: state.type,
      })
      .then(() => {
        mutate('/mentorship/mentor');
        setState({
          ...initialState,
          mentorshipId: state.mentorshipId,
        });
        setSuccess("La proposition d'entretien a été créée avec succès.");
      })
      .catch((err) => {
        if (err?.response?.data?.fields?.includes('date')) {
          return setErrors({
            ...initialErrors,
            date: true,
          });
        }
        setErrors({
          ...initialErrors,
          general: true,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Artificial interview
  const [artificialOpen, setArtificialOpen] = useState<boolean>(false);
  const [artificialLoading, setArtificialLoading] = useState<boolean>(false);
  const [artificialError, setArtificialError] = useState<string>('');
  const handleSubmitArtificial = () => {
    setArtificialLoading(true);
    setArtificialError('');

    const date = DateTime.fromFormat(
      `${state.date} ${state.time}`,
      'yyyy-MM-dd HH:mm',
      { zone: 'Europe/Paris' },
    )
      .toUTC()
      .toISO();

    privateAPI
      .post(`/artificial-interview/`, {
        mentorshipId: state.mentorshipId,
        date,
        type: state.type,
      })
      .then(() => {
        mutate('/mentorship/mentor');
        setState({
          ...initialState,
          mentorshipId: state.mentorshipId,
        });
        setArtificialOpen(false);
        setSuccess("L'entretien a bien été ajouté à l'historique.");
      })
      .catch((err) => {
        manageTextError(err, setArtificialError);
      })
      .finally(() => {
        setArtificialLoading(false);
      });
  };

  // Too close date
  const [tooCloseOpen, setTooCloseOpen] = useState<boolean>(false);

  return (
    <>
      <PageHeadContainer>
        <HeadContainer>
          <PageBack href={backTo} />
          <h1>Nouvel entretien</h1>
        </HeadContainer>
      </PageHeadContainer>
      <ArtificialModal
        open={artificialOpen}
        close={() => setArtificialOpen(false)}
        apply={handleSubmitArtificial}
        loading={artificialLoading}
        error={artificialError}
        mentorshipStatus={
          mentorships?.find((m) => m.id === state.mentorshipId)?.status || null
        }
      />
      <TooCloseModal
        open={tooCloseOpen}
        close={() => setTooCloseOpen(false)}
        mentorshipStatus={mentorship?.status || null}
      />
      <Form onSubmit={handleSubmit}>
        {success && <Success>{success}</Success>}
        {errors.general && <Error>Une erreur est survenue.</Error>}
        {errors.date && <Error>La date indiquée est déjà passée.</Error>}
        {state.mentorshipId !== -1 && !currentMentorshipCanCreateInterview && (
          <Warning>
            Vous ne pouvez pas proposer un nouvel entretien à ce mentoré pour
            l'instant.
          </Warning>
        )}
        <Select
          label="Mentoré"
          value={state.mentorshipId.toString()}
          required
          disabled={fixedMentee}
          onChange={handleChange}
          name="mentorshipId"
        >
          <option value="-1">-- Sélectionnez un mentoré --</option>
          {mentorships
            ?.filter((mentorship) => mentorship.status !== 'FINISHED_ADMIN')
            .map(
              (m, idx) => (
                // mentorshipStatusCanCreateInterview.includes(
                //   m.status as MentorshipStatusValues,
                // ) ? (
                <option
                  key={idx}
                  value={m.id?.toString()}
                >{`${m.mentee?.firstName} ${m.mentee?.lastName}`}</option>
              ),
              // ) : null,
            )}
        </Select>
        <Input
          type="date"
          label="Date de l'entretien"
          value={state.date}
          name="date"
          onChange={handleChange}
          required
          placeholder="aaaa-mm-jj (ex: 2022-05-19)"
        />
        <Input
          type="time"
          label="Heure (*)"
          value={state.time}
          name="time"
          onChange={handleChange}
          required
          placeholder="hh:mm"
        />
        <Select
          value={state.type}
          onChange={handleChange}
          name="type"
          label="Type d'entretien"
          required
        >
          <option value="">-- Sélectionner un type d'entretien --</option>
          {interviewTypes.map((type, idx) => {
            return (
              <option value={type} key={idx}>
                {INTERVIEW_TYPE[type]}
              </option>
            );
          })}
        </Select>
        <Button
          color="primary"
          loading={loading}
          disabled={
            state.mentorshipId === -1 || !currentMentorshipCanCreateInterview
          }
        >
          Proposer le créneau
        </Button>
      </Form>
      <Description>
        <p>
          Vous pouvez proposer un créneau d'entretien à{' '}
          {mentorship?.mentee?.firstName || 'votre mentoré'} à l'aide du
          formulaire ci-dessus.
        </p>
        <p>
          {mentorship?.mentee?.firstName || 'Votre mentoré'} devra alors choisir
          de l'accepter ou de le refuser.
        </p>
        <p>
          Si vous veniez à avoir un imprévu, il vous sera toujours possible
          d'annuler l'entretien depuis sa page détaillée.
        </p>
        <p>
          Vous pouvez organiser cet entretien de la façon qui vous arrange le
          plus avec {mentorship?.mentee?.firstName || 'votre mentoré'}. Il peut
          par exemple s'agir d'une rencontre phyisque, ou encore d'un appel
          téléphonique.
        </p>
        <p>
          (*) Les heures sélectionnées et indiquées sur l'application sont
          calibrées sur le fuseau horaire de Paris (UTC+01:00). Si vous vous
          trouvez sur un fuseau horaire différent, veillez à convertir l'heure
          de votre entretien pour qu'elle corresponde au fuseau horaire de
          Paris.
        </p>
        <Warning>
          Certains navigateurs, comme Safari ou Internet Explorer, proposent des
          fonctionnalités limitées, ce qui peut compliquer la prise de
          rendez-vous. Si vous rencontrez des problèmes (notamment pour
          sélectionner la date de l'entretien), nous vous conseillons de
          télécharger un nouveau navigateur, tel que Chrome ou Firefox.
        </Warning>
      </Description>
    </>
  );
};

type ArtificialModalProps = {
  open: boolean;
  close: () => void;
  apply: () => void;
  loading: boolean;
  error: string;
  mentorshipStatus: MentorshipStatusValues | null;
};
const ArtificialModal: FC<ArtificialModalProps> = ({
  open,
  close,
  apply,
  loading,
  error,
  mentorshipStatus,
}) => {
  return mentorshipStatus &&
    (
      [
        'ONGOING',
        'LATE_MENTEE',
        'LATE_MENTEE',
        'FINISHED_DATE',
      ] as MentorshipStatusValues[]
    ).includes(mentorshipStatus) ? (
    <ActionModal
      {...{
        open,
        close,
        apply,
        loading,
      }}
      title="Enregistrer un entretien passé"
      applyText="Enregistrer l'entretien"
      color="primary"
    >
      {error && <Error>{error}</Error>}
      <p>
        La date de l'entretien que vous essayez de proposer est déjà passée.
      </p>
      <Space height={0.5} />
      <p>
        Cependant, il vous est possible d'ajouter cet entretien à votre
        historique.
        <br />
        Cela vous permettra de garder un meilleur suivi de votre mentorat dans
        le cas où vous auriez rencontré votre mentoré sans avoir programmé un
        entretien depuis ce portail.
      </p>
    </ActionModal>
  ) : (
    <Modal {...{ open, close }}>
      <Typography color="error" variant="h1">
        Attention
      </Typography>
      <Space height={0.5} />
      <p>
        La date de l'entretien que vous essayez de proposer est déjà passée.
      </p>
      <Space height={0.5} />
      <p>
        Veuillez sélectionner une date valide pour proposer votre premier
        entretien.
        <br />
        Une fois votre premier entretien passé, vous pourrez ajouter à votre
        historique des entretiens dont la date est passée.
      </p>
    </Modal>
  );
};

type TooCloseModalProps = {
  open: boolean;
  close: () => void;
  mentorshipStatus: MentorshipStatusValues | null;
};
const TooCloseModal: FC<TooCloseModalProps> = ({
  open,
  close,
  mentorshipStatus,
}) => {
  return (
    <Modal open={open} close={close}>
      <Typography color="error" variant="h1">
        Attention
      </Typography>
      <Space height={0.5} />
      <p>
        La date de l'entretien que vous essayez de proposer n'est pas assez
        lointaine. Votre mentoré risque de ne pas avoir le temps d'accepter
        votre proposition.
      </p>
      <Space height={0.5} />
      <p>
        Si vous souhaitez réaliser un entretien improvisé avec votre mentoré et
        en garder une trace ce portail, vous pourrez l'ajouter à votre
        historique une fois{' '}
        {mentorshipStatus &&
        (
          [
            'ONGOING',
            'LATE_MENTEE',
            'LATE_MENTEE',
            // 'FINISHED_DATE',
          ] as MentorshipStatusValues[]
        ).includes(mentorshipStatus)
          ? 'votre entretien terminé.'
          : 'que vous passé votre premier entretien.'}
      </p>
    </Modal>
  );
};

export default InterviewSuggestForm;
